import { SelectionDropdown } from "../../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { InputNumber } from "../../../../../../../components/InputNumber/InputNumber.tsx";
import {
  rgbaStringToRgba,
  rgbaToString,
} from "../../../../../../../utils/colors.ts";
import type { StrokeStyle } from "../../../Designer/types.ts";
import { SETTINGS_ITEM_WIDTH } from "../../constants.ts";
import { DesignerColorPickerButton } from "./DesignerColorPickerButton.tsx";

type StrokeSettings = {
  strokeWidth: number;
  strokeColor: string;
  strokeStyle: StrokeStyle;
};

const STROKE_STYLES_DISPLAY_NAMES: Record<StrokeStyle, string> = {
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
  "dash-dot": "Dash-dot",
};

export const StrokeSettingsSection = ({
  strokeSettings,
  onStrokeSettingsChange,
  minThickness,
  maxThickness,
}: {
  strokeSettings: StrokeSettings;
  onStrokeSettingsChange: (strokeSettings: Partial<StrokeSettings>) => void;
  minThickness: number;
  maxThickness: number;
}) => (
  <div className="grid grid-cols-2 items-center gap-y-050 gap-x-200">
    <div className="label-md-default text-secondary">Color</div>
    <DesignerColorPickerButton
      color={rgbaStringToRgba(strokeSettings.strokeColor)}
      onColorChange={(strokeColor) =>
        onStrokeSettingsChange({
          ...strokeSettings,
          strokeColor: rgbaToString(strokeColor),
        })
      }
    />
    <div className="label-md-default text-secondary">Style</div>
    <StrokeStyleDropdown
      style={strokeSettings.strokeStyle}
      onStyleChange={(strokeStyle) =>
        onStrokeSettingsChange({ ...strokeSettings, strokeStyle })
      }
    />
    <div className="label-md-default text-secondary">Thickness</div>
    <StrokeThicknessInputNumber
      thickness={strokeSettings.strokeWidth}
      onThicknessChange={(strokeWidth) =>
        onStrokeSettingsChange({ ...strokeSettings, strokeWidth })
      }
      minValue={minThickness}
      maxValue={maxThickness}
    />
  </div>
);

const StrokeStyleDropdown = ({
  style,
  onStyleChange,
}: {
  style: StrokeStyle;
  onStyleChange: (style: StrokeStyle) => void;
}) => (
  <SelectionDropdown
    value={style}
    onChange={onStyleChange}
    options={Object.entries(STROKE_STYLES_DISPLAY_NAMES).map(
      ([key, value]) => ({
        value: key as StrokeStyle,
        label: value,
      }),
    )}
    size="sm"
    buttonClassName={SETTINGS_ITEM_WIDTH}
  />
);

const StrokeThicknessInputNumber = ({
  thickness,
  onThicknessChange,
  minValue,
  maxValue,
}: {
  thickness: number;
  onThicknessChange: (thickness: number) => void;
  minValue: number;
  maxValue: number;
}) => {
  const onIconDrag = (deltaX: number, pixelRange: number) => {
    const thicknessDelta = (deltaX / pixelRange) * (maxValue - minValue);
    const clampedThicknessDelta = Math.max(
      minValue,
      Math.min(maxValue, thickness + thicknessDelta),
    );
    onThicknessChange(Math.round(clampedThicknessDelta * 10) / 10);
  };
  return (
    <InputNumber
      min={minValue}
      max={maxValue}
      value={thickness}
      onChange={onThicknessChange}
      className={SETTINGS_ITEM_WIDTH}
      suffix="px"
      iconName="PencilLine"
      onIconDrag={(deltaX) => onIconDrag(deltaX, 100)}
    />
  );
};
